/** A plant's recipe picker, as Factorio's assembler: icon tiles under tabs
 *  by chain, an info pane for the tile under the mouse or the keyboard,
 *  the current recipe(s) highlighted, a click (or Enter) picks at once.
 *  A panel anchored where it was opened, not a modal. */

import { For, Show, createMemo, createSignal, onCleanup, onMount, type JSX } from "solid-js";
import { RECIPE_TABS, linesOf, perMinute, recipes, tabOf, ticksOf, type Recipe } from "../state/recipes";
import { Portal } from "solid-js/web";
import { goodSvg, recipeIconGood } from "../ui/goodIcons";

const COLS = 6;

/** A good's icon, `size` px. */
export function GoodIcon(props: { good: string; size?: number }): JSX.Element {
  return <span class="good-icon-wrap" innerHTML={goodSvg(props.good, props.size ?? 24)} />;
}

/** A recipe's own icon (its main output); where another recipe makes the
 *  same, its telling input in the corner (Armour steel by nickel, by
 *  chromium; a refinery with platinum). */
export function RecipeIcon(props: { recipe: string; size?: number }): JSX.Element {
  const r = () => recipes().find((x) => x.name === props.recipe);
  const good = () => (r() ? recipeIconGood(r()!) : props.recipe);
  const badge = () => {
    const me = r();
    if (!me || !me.inputs.length) return null;
    const twins = recipes().filter((x) => x !== me && recipeIconGood(x) === recipeIconGood(me));
    if (!twins.length) return null;
    // The input the others don't take.
    const own = me.inputs.find(([g]) => !twins.every((t) => t.inputs.some(([h]) => h === g)));
    return own?.[1] ?? null;
  };
  return (
    <span class="recipe-icon">
      <GoodIcon good={good()} size={props.size} />
      <Show when={badge()}>{(b) => <span class="recipe-badge"><GoodIcon good={b()} size={Math.round((props.size ?? 24) * 0.5)} /></span>}</Show>
    </span>
  );
}

const num = (n: number) => (n >= 100 ? Math.round(n).toLocaleString("en-US") : n >= 10 ? n.toFixed(0) : String(Math.round(n * 10) / 10));
const secs = (ticks: number) => {
  const s = ticks / 10;
  return s >= 60 ? `${num(s / 60)} min` : `${num(s)} s`;
};

const trains = (n: number) => (n === 1 ? "1 train" : `${num(n)} trains`);

/** What a recipe makes when it makes no good: its words and icon. */
function sideEffect(r: Recipe): { good: string; text: string } | null {
  if (r.outputs.length) return null;
  if (/locomotive/i.test(r.name)) return { good: "Train", text: "1 train" };
  if (/mint/i.test(r.name)) return { good: "Credits", text: "credits" };
  return null;
}

/** One side of a recipe: icons with amounts, `per` choosing a cycle's or a
 *  minute's. */
function Goods(props: { r: Recipe; list: [number, string, number][]; minute: boolean; lines?: number }): JSX.Element {
  return (
    <For each={props.list}>
      {([, g, n]) => (
        <span class="recipe-good" data-tip={g}>
          <GoodIcon good={g} size={20} />
          <span>{props.minute ? num(perMinute(props.r, n, props.lines)) : num(n)}</span>
        </span>
      )}
    </For>
  );
}

/** A recipe's inputs then outputs as icons and amounts. */
export function RecipeFlow(props: { r: Recipe; minute: boolean; lines?: number }): JSX.Element {
  const extra = () => sideEffect(props.r);
  return (
    <div class="recipe-flow">
      <Show when={props.r.inputs.length} fallback={<span class="muted">nothing</span>}>
        <Goods r={props.r} list={props.r.inputs} minute={props.minute} lines={props.lines} />
      </Show>
      <span class="recipe-arrow">→</span>
      <Goods r={props.r} list={props.r.outputs} minute={props.minute} lines={props.lines} />
      <Show when={extra()}>
        {(x) => (
          <span class="recipe-good">
            <GoodIcon good={x().good} size={20} />
            <span>{props.minute && x().good === "Train" ? trains(perMinute(props.r, 1, props.lines)) : x().text}</span>
          </span>
        )}
      </Show>
    </div>
  );
}

/** The info pane (Factorio's recipe tooltip): name, a cycle's goods and
 *  its time, and the rates a minute at the plant's lines. */
export function RecipeInfo(props: { r: Recipe; lines?: number; cost?: string }): JSX.Element {
  const lines = () => props.lines ?? linesOf(props.r);
  return (
    <div class="recipe-info">
      <div class="recipe-info-name">
        {props.r.name}
        <Show when={props.cost}>
          <span class="recipe-cost">{props.cost}</span>
        </Show>
      </div>
      <div class="recipe-info-row">
        <span class="row-label">A cycle, {secs(ticksOf(props.r))}</span>
        <RecipeFlow r={props.r} minute={false} lines={lines()} />
      </div>
      <div class="recipe-info-row">
        <span class="row-label">A minute, {lines() === 1 ? "1 line" : `${lines()} lines`}</span>
        <RecipeFlow r={props.r} minute={true} lines={lines()} />
      </div>
      <Show when={/mint/i.test(props.r.name)}>
        <p class="muted small">Credits at copper's worth, and the copper's gone.</p>
      </Show>
      <Show when={props.r.inputs.length}>
        <p class="muted small">Inputs come to its station by rail; outputs leave the same way.</p>
      </Show>
    </div>
  );
}

/** The grid: tabs, tiles, and the info pane. `current` are the recipes in
 *  use (highlighted); `pick` is called with a tile's recipe. */
export function RecipePicker(props: { current: string[]; pick: (name: string) => void; close: () => void; anchor: HTMLElement; align?: "left" | "right"; cost?: (name: string) => string }): JSX.Element {
  let root!: HTMLDivElement;
  // Fixed above its opener, kept on screen (a panel over the console and
  // the map, so nothing clips it).
  const [pos, setPos] = createSignal<{ left: number; bottom: number }>({ left: 0, bottom: 0 });
  const place = () => {
    const a = props.anchor.getBoundingClientRect();
    const w = root?.offsetWidth ?? 760;
    const want = props.align === "right" ? a.right - w : a.left;
    setPos({ left: Math.max(8, Math.min(window.innerWidth - w - 8, want)), bottom: window.innerHeight - a.top + 6 });
  };
  const tabs = createMemo(() => {
    const all = recipes();
    const out = RECIPE_TABS.map((t) => ({ name: t.name, icon: t.icon, list: all.filter((r) => tabOf(r.name) === t.name) })).filter((t) => t.list.length);
    const other = all.filter((r) => tabOf(r.name) === "Other");
    if (other.length) out.push({ name: "Other", icon: recipeIconGood(other[0]), list: other });
    return out;
  });
  const [tab, setTab] = createSignal(tabOf(props.current[0] ?? "") );
  const shown = () => tabs().find((t) => t.name === tab()) ?? tabs()[0];
  const [focus, setFocus] = createSignal(Math.max(0, shown()?.list.findIndex((r) => props.current.includes(r.name)) ?? 0));
  const [hover, setHover] = createSignal<Recipe | null>(null);
  const info = () => hover() ?? shown()?.list[focus()] ?? null;

  const switchTab = (d: number) => {
    const list = tabs();
    const i = list.findIndex((t) => t.name === shown()?.name);
    const next = list[(i + d + list.length) % list.length];
    setTab(next.name);
    setFocus(Math.max(0, next.list.findIndex((r) => props.current.includes(r.name))));
    setHover(null);
  };
  const onKey = (e: KeyboardEvent) => {
    const n = shown()?.list.length ?? 0;
    const move = (to: number) => {
      e.preventDefault();
      setHover(null);
      setFocus(Math.max(0, Math.min(n - 1, to)));
    };
    if (e.key === "ArrowRight") move(focus() + 1);
    else if (e.key === "ArrowLeft") move(focus() - 1);
    else if (e.key === "ArrowDown") move(focus() + COLS < n ? focus() + COLS : focus());
    else if (e.key === "ArrowUp") move(focus() - COLS >= 0 ? focus() - COLS : focus());
    else if (e.key === "Tab") {
      e.preventDefault();
      switchTab(e.shiftKey ? -1 : 1);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const r = shown()?.list[focus()];
      if (r) props.pick(r.name);
    } else if (e.key === "Escape") {
      e.preventDefault();
      props.close();
    } else return;
    // The panel has the keyboard: nothing reaches the map's keys.
    e.stopPropagation();
  };
  // Swallow every key while open (letters would be map orders).
  const swallow = (e: KeyboardEvent) => {
    if (root.contains(e.target as Node)) e.stopPropagation();
  };
  onMount(() => {
    place();
    requestAnimationFrame(place);
    window.addEventListener("resize", place);
    onCleanup(() => window.removeEventListener("resize", place));
    root.focus();
    const away = (e: PointerEvent) => {
      const t = e.target as Element;
      if (!root.contains(t) && !t.closest?.("[data-recipe-opener]")) props.close();
    };
    document.addEventListener("pointerdown", away, true);
    onCleanup(() => document.removeEventListener("pointerdown", away, true));
  });

  return (
    <Portal>
    <div
      ref={root}
      class="recipe-picker"
      style={{ left: `${pos().left}px`, bottom: `${pos().bottom}px` }}
      tabindex="0"
      role="dialog"
      aria-label="Choose a recipe"
      onKeyDown={(e) => {
        onKey(e);
        swallow(e);
      }}
    >
      <div class="recipe-tabs" role="tablist">
        <For each={tabs()}>
          {(t) => (
            <button
              role="tab"
              class="recipe-tab"
              aria-selected={t.name === shown()?.name}
              data-tip={t.name}
              onClick={() => {
                setTab(t.name);
                setFocus(Math.max(0, t.list.findIndex((r) => props.current.includes(r.name))));
                root.focus();
              }}
            >
              <GoodIcon good={t.icon} size={28} />
              <span>{t.name}</span>
            </button>
          )}
        </For>
      </div>
      <div class="recipe-body">
        <div class="recipe-grid" style={{ "grid-template-columns": `repeat(${COLS}, 56px)` }} onPointerLeave={() => setHover(null)}>
          <For each={shown()?.list ?? []}>
            {(r, i) => (
              <button
                class="recipe-tile"
                classList={{ current: props.current.includes(r.name), focused: i() === focus() }}
                aria-label={r.name}
                onPointerEnter={() => setHover(r)}
                onClick={() => {
                  setFocus(i());
                  props.pick(r.name);
                }}
              >
                <RecipeIcon recipe={r.name} size={36} />
                <Show when={props.cost?.(r.name).startsWith("Free")}>
                  <span class="recipe-free">Free</span>
                </Show>
              </button>
            )}
          </For>
        </div>
        <Show when={info()}>{(r) => <RecipeInfo r={r()} cost={props.cost?.(r().name)} />}</Show>
      </div>
    </div>
    </Portal>
  );
}
